import type { KeyframeTrack } from 'three';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

const POSITION_TRACK_SUFFIX = '.position';

function toBoneName(trackName: string): string | null {
  if (!trackName.endsWith(POSITION_TRACK_SUFFIX)) return null;
  return trackName.slice(0, -POSITION_TRACK_SUFFIX.length);
}

/** True when trackName addresses any property on a joint listed in morphJoints. */
export function isMorphTrack(
  trackName: string,
  morphJoints: Set<string>,
): boolean {
  const lastDot = trackName.lastIndexOf('.');
  if (lastDot <= 0) return false;
  return morphJoints.has(trackName.slice(0, lastDot));
}

function rootLikePriority(boneName: string): number {
  const normalized = boneName.toLowerCase();
  if (normalized === 'root') return 0;
  if (normalized.includes('root')) return 1;
  if (normalized.includes('hips')) return 2;
  if (normalized.includes('pelvis')) return 3;
  if (normalized.includes('waist')) return 4;
  return 10;
}

/**
 * Sum of Euclidean distances between consecutive keyframe positions (path length).
 * Picks the track that carries the most translational motion (e.g. pelvis vs static root).
 */
export function computePositionTrackPathLength(track: KeyframeTrack): number {
  const values = track.values;
  const valueSize = track.getValueSize();
  if (valueSize < 1 || values.length < valueSize * 2) return 0;

  const n = values.length / valueSize;
  let sum = 0;
  for (let i = 1; i < n; i++) {
    let distSq = 0;
    for (let c = 0; c < valueSize; c++) {
      const cur = i * valueSize + c;
      const prev = (i - 1) * valueSize + c;
      const d = values[cur] - values[prev];
      distSq += d * d;
    }
    sum += Math.sqrt(distSq);
  }
  return sum;
}

export function isPositionTrackName(trackName: string): boolean {
  return trackName.endsWith(POSITION_TRACK_SUFFIX);
}

/**
 * Picks the *.position track with the largest path length (most translational motion).
 * Tie-break: root-like joint names, then track order.
 * morphJoints, when provided, excludes single-Xposition morph driver joints from the contest.
 */
export function computePrimaryPositionTrackNameFromTracks(
  clipTracks: readonly KeyframeTrack[],
  morphJoints?: Set<string>,
): string | null {
  if (clipTracks.length === 0) return null;

  let bestName: string | null = null;
  let bestScore = -1;
  let bestPriority = Number.POSITIVE_INFINITY;
  let bestIndex = Number.POSITIVE_INFINITY;

  for (let i = 0; i < clipTracks.length; i += 1) {
    const track = clipTracks[i];
    const trackName = track.name;
    if (!isPositionTrackName(trackName)) continue;
    if (morphJoints && isMorphTrack(trackName, morphJoints)) continue;

    const score = computePositionTrackPathLength(track);
    const boneName = toBoneName(trackName);
    const priority = boneName == null ? 99 : rootLikePriority(boneName);

    if (score > bestScore) {
      bestScore = score;
      bestName = trackName;
      bestPriority = priority;
      bestIndex = i;
    } else if (score === bestScore) {
      if (priority < bestPriority) {
        bestName = trackName;
        bestPriority = priority;
        bestIndex = i;
      } else if (priority === bestPriority && i < bestIndex) {
        bestName = trackName;
        bestIndex = i;
      }
    }
  }

  return bestName;
}

export function resolvePrimaryPositionTrackName(bvh: BVH): string | null {
  const userData = (
    bvh.clip as unknown as { userData?: Record<string, unknown> }
  ).userData;
  const morphJoints = userData?.morphJoints as Set<string> | undefined;
  return computePrimaryPositionTrackNameFromTracks(
    bvh.clip.tracks ?? [],
    morphJoints,
  );
}
